import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { autoBreadcrumbs, breadcrumbList } from "@/utils/breadcrumb";

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  /** Override the canonical URL. By default we build it from window.location. */
  url?: string;
  type?: string;
  /** Optional extra structured-data object — appended after the default Organization block. */
  schema?: object;
  noindex?: boolean;
  canonical?: string;
}

// Centralized site URL (env-driven, fallback to the registered domain).
// Override via VITE_SITE_URL in .env or .env.production.
import { SITE_URL } from "@/lib/site-config";
const DEFAULT_IMAGE = `${SITE_URL}/og-image.jpg`;

// Kept ≤60 / ≤160 chars so the SERP snippet doesn't truncate and so the JS-
// rendered home matches the static baseline in index.html (non-JS crawlers
// and the JS render then see the same identity). Long-tail keywords still
// live in the keywords meta and in per-page <SEO title=…> overrides where
// they don't crowd the snippet.
// Default title + description aligned with the deep-research findings
// (Dr. Reddy's pattern + Hangzhou-Longshine disambiguation): brand is
// front-loaded so we are not pre-empted by the Chinese namesake on
// "Longshine" brand queries, then four certification keywords + Pune
// geo modifier ride along to capture B2B procurement intent.
const DEFAULT_TITLE =
  "Longshine Boi-Tech — WHO-GMP Pharmaceutical APIs Manufacturer, Pune, India";
const DEFAULT_DESCRIPTION =
  "WHO-GMP, ISO 9001:2015, US FDA DMF, EDQM CEP certified API manufacturer in Pune. 560+ pharmaceutical APIs, intermediates, peptides, veterinary. CDMO services.";
const DEFAULT_KEYWORDS =
  "pharmaceutical APIs, WHO-GMP API supplier, FDA DMF API manufacturer, CEP EDQM supplier, drug intermediates, veterinary APIs, ISO 9001:2015, CAS numbers, API manufacturer Pune India, custom synthesis, CDMO contract manufacturing";

const SEO = ({
  title,
  description,
  keywords,
  image = DEFAULT_IMAGE,
  url,
  type = "website",
  schema,
  noindex = false,
  canonical,
}: SEOProps) => {
  const { language } = useLanguage();
  const location = useLocation();

  const seoTitle = title ? `${title} | Longshine Boi-Tech` : DEFAULT_TITLE;
  const seoDescription = description || DEFAULT_DESCRIPTION;
  const seoKeywords = keywords || DEFAULT_KEYWORDS;
  const canonicalUrl = canonical || url || `${SITE_URL}${location.pathname}`;

  // Social scrapers (Facebook, LinkedIn, Slack) require an ABSOLUTE og:image
  // URL — a relative "/assets/x.jpg" silently fails to unfurl. Product pages
  // pass a relative product image, so absolutize anything that isn't already
  // a full URL (http/https) or a data: URI.
  const absoluteImage = /^(https?:|data:)/.test(image)
    ? image
    : `${SITE_URL}${image.startsWith("/") ? "" : "/"}${image}`;

  // react-helmet-async strips raw <script> children for safety, so per-page
  // JSON-LD has to be injected directly into document.head. We tag the
  // node with data-seo-schema so the cleanup runs even if a parent rerenders.
  useEffect(() => {
    if (!schema) return;
    const node = document.createElement("script");
    node.type = "application/ld+json";
    node.setAttribute("data-seo-schema", "per-page");
    node.textContent = JSON.stringify(schema);
    document.head.appendChild(node);
    return () => {
      node.remove();
    };
  }, [schema]);

  // Auto-inject BreadcrumbList schema for every non-home route. This is
  // additive — Google handles multiple JSON-LD blocks just fine — so it
  // composes with whatever per-page `schema` the page passed in. Means
  // every product, category, and content page becomes eligible for the
  // breadcrumb rich-result in the SERP without per-page wiring.
  useEffect(() => {
    if (noindex) return;                            // noindex'd pages don't need crumbs
    const crumbs = autoBreadcrumbs(location.pathname);
    if (!crumbs || crumbs.length < 2) return;       // single-item list isn't useful
    const node = document.createElement("script");
    node.type = "application/ld+json";
    node.setAttribute("data-seo-schema", "breadcrumb");
    node.textContent = JSON.stringify({
      "@context": "https://schema.org",
      ...breadcrumbList(crumbs),
    });
    document.head.appendChild(node);
    return () => { node.remove(); };
  }, [location.pathname, noindex]);

  // Organization schema is already inlined in index.html. Per-page schema
  // (passed via `schema`) is rendered as an additional script. This keeps
  // baseline structured data discoverable without JS while still allowing
  // per-page extensions (Product, Article, FAQ, BreadcrumbList).

  // Map our LanguageContext code to a full BCP-47 locale that Open Graph
  // and search engines expect. en/zh/es → en_US / zh_CN / es_ES.
  const ogLocale = language === "zh" ? "zh_CN" : language === "es" ? "es_ES" : "en_US";
  // Mirror onto the root <html lang> via Helmet — without this, Google
  // treats the entire DOM as English-only, undermining the hreflang
  // tags we emit further down.
  const htmlLang = language === "zh" ? "zh" : language === "es" ? "es" : "en";

  return (
    <Helmet htmlAttributes={{ lang: htmlLang }}>
      {/* Core meta */}
      <title>{seoTitle}</title>
      <meta name="description" content={seoDescription} />
      <meta name="keywords" content={seoKeywords} />
      <meta name="author" content="Longshine Boi-Tech Pvt. Ltd." />
      <meta name="application-name" content="Longshine Boi-Tech" />
      <meta name="apple-mobile-web-app-title" content="Longshine Boi-Tech" />
      <meta name="theme-color" content="#0f172a" />
      <meta
        name="robots"
        content={noindex ? "noindex,nofollow" : "index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1"}
      />
      <meta name="language" content={language} />

      {/* Canonical — always emit one so each page has a single authoritative URL */}
      <link rel="canonical" href={canonicalUrl} />

      {/* hreflang — broadcast all 11 languages the LanguageProvider
          supports. With country pages now serving language-localized
          intros (PT for /country/brazil, DE for /country/germany, ES
          for /country/mexico) Google can confidently map each search
          query's locale to the right variant. Same URL for all
          (single-route SPA) — Google accepts this when paired with
          per-request language detection in our LanguageContext.
          x-default points to the canonical English URL — fallback for
          unidentified locales (Yandex, Naver). */}
      <link rel="alternate" hrefLang="en"    href={canonicalUrl} />
      <link rel="alternate" hrefLang="es"    href={canonicalUrl} />
      <link rel="alternate" hrefLang="es-MX" href={canonicalUrl} />
      <link rel="alternate" hrefLang="es-AR" href={canonicalUrl} />
      <link rel="alternate" hrefLang="pt"    href={canonicalUrl} />
      <link rel="alternate" hrefLang="pt-BR" href={canonicalUrl} />
      <link rel="alternate" hrefLang="de"    href={canonicalUrl} />
      <link rel="alternate" hrefLang="fr"    href={canonicalUrl} />
      <link rel="alternate" hrefLang="it"    href={canonicalUrl} />
      <link rel="alternate" hrefLang="ja"    href={canonicalUrl} />
      <link rel="alternate" hrefLang="ko"    href={canonicalUrl} />
      <link rel="alternate" hrefLang="zh"    href={canonicalUrl} />
      <link rel="alternate" hrefLang="zh-CN" href={canonicalUrl} />
      <link rel="alternate" hrefLang="ru"    href={canonicalUrl} />
      <link rel="alternate" hrefLang="ar"    href={canonicalUrl} />
      <link rel="alternate" hrefLang="hi"    href={canonicalUrl} />
      <link rel="alternate" hrefLang="x-default" href={canonicalUrl} />

      {/* Geographic targeting */}
      <meta name="geo.region" content="IN-MH" />
      <meta name="geo.placename" content="Pune, Maharashtra, India" />
      <meta name="geo.position" content="18.5204;73.8567" />
      <meta name="ICBM" content="18.5204, 73.8567" />

      {/* Business categorization */}
      <meta name="industry" content="Pharmaceutical Manufacturing" />
      <meta name="business-type" content="B2B Pharmaceutical Supplier" />

      {/* Open Graph. Image dimensions + type + alt are required for
          Facebook/LinkedIn/Slack to render proper cards — without them
          the unfurled preview is often blank or low-res. */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={seoTitle} />
      <meta property="og:description" content={seoDescription} />
      <meta property="og:image" content={absoluteImage} />
      <meta property="og:image:type" content="image/jpeg" />
      <meta property="og:image:width" content="1920" />
      <meta property="og:image:height" content="1080" />
      <meta property="og:image:alt" content={title || "Longshine Boi-Tech pharmaceutical manufacturing"} />
      <meta property="og:site_name" content="Longshine Boi-Tech" />
      <meta property="og:locale" content={ogLocale} />
      {/* og:locale:alternate — broadcast every supported locale so
          social platforms (LinkedIn, Facebook) attempt the right
          translation when a buyer in that locale shares the URL. */}
      <meta property="og:locale:alternate" content="en_US" />
      <meta property="og:locale:alternate" content="en_GB" />
      <meta property="og:locale:alternate" content="es_ES" />
      <meta property="og:locale:alternate" content="es_MX" />
      <meta property="og:locale:alternate" content="pt_BR" />
      <meta property="og:locale:alternate" content="de_DE" />
      <meta property="og:locale:alternate" content="fr_FR" />
      <meta property="og:locale:alternate" content="it_IT" />
      <meta property="og:locale:alternate" content="ja_JP" />
      <meta property="og:locale:alternate" content="ko_KR" />
      <meta property="og:locale:alternate" content="zh_CN" />
      <meta property="og:locale:alternate" content="zh_TW" />
      <meta property="og:locale:alternate" content="ru_RU" />
      <meta property="og:locale:alternate" content="ar_AE" />
      <meta property="og:locale:alternate" content="hi_IN" />
      <meta property="og:url" content={canonicalUrl} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={seoTitle} />
      <meta name="twitter:description" content={seoDescription} />
      <meta name="twitter:image" content={absoluteImage} />
      <meta name="twitter:image:alt" content={title || "Longshine Boi-Tech pharmaceutical manufacturing"} />

      {/* Per-page structured data is injected via useEffect above — Helmet
          children don't support raw <script>. The site-wide Organization +
          WebSite blocks live in index.html so non-JS crawlers see them. */}

      {/* Performance hints */}
      <link rel="dns-prefetch" href="//fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
    </Helmet>
  );
};

export default SEO;
